import React, { useCallback, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import styled from 'styled-components'
import { Token } from '@nominex/nomiswap-sdk'
import { SlButton, SlIcon, SlIconButton, SlInput } from '@shoelace-style/shoelace/dist/react'
import { useToken } from 'hooks/Tokens'
import { useRemoveUserAddedToken } from 'state/user/hooks'
import useUserAddedTokens from 'state/user/hooks/useUserAddedTokens'
import QuestionHelper from 'components/QuestionHelper'
import { Row } from 'components/Layout'
import { CurrencyImage } from 'components/CurrencyImage'
import { getBlockExplorerLink, isAddress } from 'utils'
import { useWagmi } from 'libs/wagmi'
import { useTranslation } from 'contexts/Localization'
import { AutoColumn, Column } from '../Layout'
import ImportRow from './ImportRow'
import { CurrencyModalView } from './types'

const Wrapper = styled.div`
  width: 100%;
  height: calc(100% - 60px);
  position: relative;
  padding-bottom: 60px;
`

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CurrencyRow = styled.div`
  ${tw`text-white grid gap-8 items-center px-20 py-4 rounded-12 hover:bg-dark-gray-900 h-[56px]`}
  grid-template-columns: auto minmax(auto, 1fr) minmax(0, 80px);
`

const StyledCurrencyLink = styled.a`
  ${tw`text-16 font-semibold text-blue hover:text-blue-400`}
`

export default function ManageTokens({
  setModalView,
  setImportToken,
}: {
  setModalView: (view: CurrencyModalView) => void
  setImportToken: (token: Token) => void
}) {
  const { chainId } = useWagmi()

  const { t } = useTranslation()

  const [searchQuery, setSearchQuery] = useState<string>('')

  // manage focus on modal show
  const inputRef = useRef<typeof SlInput>()

  const handleInput = useCallback((event) => {
    const checksummedInput = isAddress(event.target.value)
    setSearchQuery(checksummedInput || event.target.value)
  }, [])

  // if they input an address, use it
  const searchToken = useToken(searchQuery)

  // all tokens for local list
  const userAddedTokens: Token[] = useUserAddedTokens()
  const removeToken = useRemoveUserAddedToken()

  const handleRemoveAll = useCallback(() => {
    if (chainId && userAddedTokens) {
      userAddedTokens.map((token) => removeToken(chainId, token.address))
    }
  }, [removeToken, userAddedTokens, chainId])

  const tokenList = useMemo(
    () =>
      chainId &&
      userAddedTokens.map((token) => (
        <CurrencyRow key={token.address}>
          <CurrencyImage currency={token} size="32px" />
          <div className="flex flex-col">
            <StyledCurrencyLink href={getBlockExplorerLink(token.address, 'address', chainId)} target="_blank">
              {token.symbol}
            </StyledCurrencyLink>
            <span className="text-14 text-dark-gray-100">{token.name}</span>
          </div>
          <SlIconButton
            className="justify-self-end"
            name="trash-alt"
            library="unicons"
            slot="trigger"
            onClick={() => removeToken(chainId, token.address)}
          />
        </CurrencyRow>
      )),
    [userAddedTokens, chainId, removeToken],
  )

  const isAddressValid = searchQuery === '' || isAddress(searchQuery)

  return (
    <Wrapper>
      <Column style={{ width: '100%', flex: '1 1' }}>
        <AutoColumn gap="14px">
          <Row>
            <SlInput
              className="w-full"
              id="token-search-input"
              size="large"
              placeholder="0x0000"
              value={searchQuery}
              autocomplete="off"
              ref={inputRef}
              onSlInput={handleInput}
              invalid={searchQuery === '' || isAddress(searchQuery) === false}
            >
              {!isAddressValid && <div slot="help-text">{t('Please enter valid token address')}</div>}
            </SlInput>
          </Row>
          {searchToken && (
            <ImportRow
              token={searchToken}
              showImportView={() => setModalView(CurrencyModalView.importToken)}
              setImportToken={setImportToken}
              style={{ height: 'fit-content' }}
            />
          )}
        </AutoColumn>

        <div className="pt-24">{tokenList}</div>

        <Footer>
          <span className="text-14 text-dark-gray-100">
            <span className="font-semibold lowercase">{userAddedTokens?.length}</span>{' '}
            {userAddedTokens.length === 1 ? t('Custom token') : t('Custom tokens')}
            <QuestionHelper content={t('Custom tokens are stored locally in your browser')} />
          </span>
          {userAddedTokens.length > 0 && (
            <SlButton variant="text" onClick={handleRemoveAll}>
              {t('Remove all')}
              <SlIcon name="trash-alt" library="unicons" slot="prefix" />
            </SlButton>
          )}
        </Footer>
      </Column>
    </Wrapper>
  )
}
